"use client";

import Image from "next/image";
import { useRef } from "react";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";
import { useInView } from "@/hooks/useInView";
import EnrollTourButtons from "@/components/cms/EnrollTourButtons";
import { motion } from "framer-motion";


export default function AboutHero({ cms, logoUrl }: { cms?: Record<string, string>; logoUrl?: string | null }) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);
  // Animation setup for heading and paragraph using scroll-based in-view detection
  const headingRef = useRef<HTMLDivElement>(null);
  const showHeading = useInView(headingRef, {
    margin: "0px",
  });
  const bgImage = cms?.bg_image_url ?? ""; // || "https://muqh1zzqjnwuke9n.public.blob.vercel-storage.com/about/20240514-_MG_1413.webp"

  return (
    <section className="relative min-h-screen flex items-center justify-center font-nunito overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={parallaxRef}
          className="will-change-transform"
          style={{
            top: "-10%",
            height: "120%",
            width: "100%",
            position: "absolute",
            transform: isDesktop ? transform3d : undefined,
          }}
        >
          <Image
            src={bgImage}
            alt="Students in classroom"
            fill
            sizes="100vw"
            className="object-cover"
            priority={false}
            loading="eager"
            placeholder="blur"
            blurDataURL="data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA="
          />
        </div>
        <div
          className="absolute inset-0"
          style={{ background: cms?.overlay_color ?? "rgba(6,22,82,0.6)" }}
        ></div>
      </div>
      {/* Consistent content wrapper */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto flex flex-col items-center pt-12 pb-12 sm:pt-24 sm:pb-24">
        {/* Consistent logo spacing */}
        {/* School Logo */}
        {logoUrl && (
        <div className="mb-8 pt-8">
          <Image
            src={logoUrl}
            alt="Ashton John's Private School Logo"
            width={200}
            height={200}
            className="mx-auto w-52 h-48"
            priority
          />
        </div>
        )}
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 60 }}
          animate={showHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 120, damping: 20, duration: 0.6 }}
          className="space-y-4 mb-12"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8">
            {cms?.heading /* || "WE BELIEVE" */}
          </h1>
          {cms?.subheading && (
          <p className="text-lg sm:text-xl md:text-2xl leading-relaxed mb-12 font-light">
            {cms.subheading}
          </p>
          )}
        </motion.div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <EnrollTourButtons
            enrollText={cms?.enrol_btn_text}
            tourText={cms?.tour_btn_text}
            enrollUrl={cms?.enrol_btn_url}
            tourUrl={cms?.tour_btn_url}
          />
        </div>
      </div>
    </section>
  );
}
